import { createFileRoute } from '@tanstack/react-router';
import Hero from '../../components/Hero';
import { basicHead } from '../../utility/head';

export const Route = createFileRoute('/_marketing/terms')({
  component: RouteComponent,
  ssr: true,
  loader: ({ location }) => ({
    pathname: location.pathname,
    seo: {
      title: 'Terms of Service - Stark Crawl',
      description:
        'Read the Stark Crawl terms of service governing your use of our web crawling and structured data platform.',
      keywords: 'Stark Crawl terms of service, terms and conditions',
    },
  }),
  head: basicHead,
});

function RouteComponent() {
  return (
    <section className="py-[3.75rem] lg:py-20 px-5 border-b border-grey-t-800 grow">
      <Hero
        breadcrumb="Terms of Service"
        title="Terms of Service"
        pathname="/terms"
      />
      <article className="mt-5 space-y-3 lg:mt-[3.75rem] max-w-[57.5rem] mx-auto prose prose-lg">
        <p className="text-sm text-gray-500">Last updated: July 2026</p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>1. Introduction</h2>
        <p>
          These Terms of Service (&quot;Terms&quot;) govern your access to and use of Stark Crawl, a service provided by Aduro Creative Ltd, a company registered in England and Wales (Company Number: 11200639), trading as Stark Crawl (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;).
        </p>
        <p>
          By creating an account or using our services, you agree to be bound by these Terms. If you do not agree to these Terms, you may not use our services.
        </p>
        <p>
          If you are using Stark Crawl on behalf of an organisation, you represent and warrant that you have the authority to bind that organisation to these Terms, and &quot;you&quot; refers to both you individually and that organisation.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>2. Description of Service</h2>
        <p>
          Stark Crawl is a platform that enables you to:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>Discover hosts and crawl websites for market research</li>
          <li>Extract structured fields and capture prospects from web sources</li>
          <li>Organise crawl projects, rules, and research outputs in one workspace</li>
          <li>Share projects with teammates via email-based access rules</li>
        </ul>
        <p>
          Our service processes crawl targets and extracted content to produce structured research data. Where AI assists extraction or classification, it does not train on your data and your content remains isolated from other users.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>3. Account Registration</h2>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>3.1 Account Creation</h3>
        <p>
          To use Stark Crawl, you must create an account by providing accurate and complete information, including your name and email address. You are responsible for maintaining the accuracy of this information.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>3.2 Account Security</h3>
        <p>
          You are responsible for safeguarding your account credentials and for all activities that occur under your account. You must notify us immediately at{' '}
          <a href="mailto:privacy@starkcrawl.com">privacy@starkcrawl.com</a>{' '}
          if you become aware of any unauthorised use of your account.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>3.3 Account Eligibility</h3>
        <p>
          Our services are intended for business and professional use. By creating an account, you represent that you have the legal capacity to enter into these Terms.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>4. Your Content</h2>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>4.1 Ownership</h3>
        <p>
          You retain all ownership rights in the content you upload to Stark Crawl (&quot;Your Content&quot;). We do not claim any ownership over Your Content.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>4.2 Licence to Us</h3>
        <p>
          By uploading content to Stark Crawl, you grant us a limited, non-exclusive, worldwide licence to process, store, convert, and display Your Content solely for the purpose of providing and improving our services to you. This includes:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>Converting files to markdown or plain text format</li>
          <li>Generating and storing text embeddings and content chunks</li>
          <li>Processing queries against Your Content using AI technology</li>
          <li>Displaying content chunks to users you have authorised</li>
        </ul>
        <p>
          This licence terminates when you delete Your Content or close your account, subject to our data retention policy.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>4.3 Your Responsibilities</h3>
        <p>
          You represent and warrant that:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>You own or have the necessary rights and permissions to upload Your Content</li>
          <li>Your Content does not infringe the intellectual property rights of any third party</li>
          <li>Your Content does not violate any applicable laws or regulations</li>
          <li>You have obtained any necessary consents to share personal data contained in Your Content</li>
        </ul>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>4.4 Shared Content</h3>
        <p>
          When you share a project publicly or grant access via email rules, you are responsible for ensuring you have the right to share that content. Content chunks (but not original source files) will become accessible to those you authorise.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>5. Acceptable Use</h2>
        <p>
          You agree not to use Stark Crawl to:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>Upload, store, or share any content that is unlawful, harmful, threatening, abusive, defamatory, or otherwise objectionable</li>
          <li>Upload content containing malware, viruses, or other harmful code</li>
          <li>Infringe the intellectual property rights of others</li>
          <li>Violate the privacy or data protection rights of others</li>
          <li>Attempt to gain unauthorised access to our systems or other users&apos; accounts</li>
          <li>Interfere with or disrupt the integrity or performance of our services</li>
          <li>Use automated means to access our services without our prior written consent</li>
          <li>Reverse engineer, decompile, or attempt to extract the source code of our software</li>
          <li>Use our services to develop a competing product or service</li>
          <li>Resell or redistribute our services without our prior written consent</li>
        </ul>
        <p>
          We reserve the right to suspend or terminate your access if we reasonably believe you have violated these acceptable use terms.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>6. Third-Party Integrations</h2>
        <p>
          Stark Crawl allows you to connect third-party services including Google Drive, Notion, and Dropbox. When you connect these services:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>You authorise us to access content from these services on your behalf</li>
          <li>You are responsible for ensuring you have the right to access and use that content</li>
          <li>Your use of third-party services remains subject to their respective terms of service</li>
          <li>We are not responsible for the availability, accuracy, or content of third-party services</li>
        </ul>
        <p>
          You may disconnect third-party integrations at any time through your account settings.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>7. Usage and Token Consumption</h2>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>7.1 Usage-Based Billing Model</h3>
        <p>
          Stark Crawl operates on a usage-based model measured in two units: queries and page imports. All AI functionality on the platform consumes one of these units, which are deducted from your available monthly allowance.
        </p>
        <p>
          A query represents an allocation of up to 50,000 tokens of processing used to answer a message, including the retrieval of relevant content and the generation of the AI response. A page import represents 1,000 tokens of processed content. A typical page of standard text is around 1,000 tokens, so importing it consumes a proportionate fraction of a page import; dense or complex documents and imported web content may consume more than one.
        </p>
        <p>
          The following types of activity consume your allowance:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>
            <strong>Embedding operations:</strong> Generated when you upload files or sync content from connected sources. Your content is processed and converted into embeddings to enable search and retrieval functionality. These consume page imports.
          </li>
          <li>
            <strong>Query operations:</strong> Generated when users send messages, including the retrieval of relevant content and the generation of AI responses. These consume queries.
          </li>
          <li>
            <strong>Reranking and retrieval operations:</strong> Performed automatically as part of query processing to improve response quality.
          </li>
        </ul>
        <p>
          The effective token consumption of any operation is determined at the time of processing based on the AI models in use. We may update these rates from time to time as set out in section 8.7.
        </p>
        <p>
          Allowances are consumed in order of earliest expiry first.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>7.2 Subscription Plans and Included Usage</h3>
        <p>
          The Essentials plan includes a monthly allowance of queries and page imports, sized by the usage level you select. The available levels, their allowances, and their prices are published on our{' '}
          <a href="/pricing">pricing page</a>. You may change your selected level at any time as set out in section 8.1.
        </p>
        <p>
          Included subscription usage expires at the end of each billing period and does not roll over to the next period.
        </p>
        <p>
          If you exhaust your included usage before the end of a billing period, the affected functionality is paused until your plan renews or you move to a higher usage level. Running out of queries pauses messages; running out of page imports pauses new imports. Each is independent of the other.
        </p>
        <p>
          For Scale customers, billing terms are agreed separately and may include custom usage allocations, custom query depth limits, API access, and invoice-based payment as set out in section 8.1.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>7.3 Query Depth</h3>
        <p>
          Each individual query processed through Stark Crawl is subject to a maximum token allocation (&quot;query depth&quot;). By default a query may consume up to 50,000 tokens. Query depth can be configured per knowledge base as a multiplier:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>
            <strong>Essentials plan:</strong> configurable from 1x to 3x (up to 150,000 tokens per query). A query configured at 2x or 3x consumes 2 or 3 queries respectively from your monthly allowance.
          </li>
          <li>
            <strong>Scale plan:</strong> no query depth cap by default; custom limits may be configured.
          </li>
        </ul>
        <p>
          Queries that would exceed the applicable query depth will be rejected with an error response. Query depth is a technical safeguard against unexpected costs and supports the predictability of usage consumption.
        </p>
        <p>
          The maximum query depth for your account is shown in your account settings.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>7.4 Liability for All Project Usage</h3>
        <p>
          You are responsible for and will be charged for all usage on your projects, whether those projects are private or public. This includes:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>Usage generated by you directly</li>
          <li>Usage generated by users you have granted access to via email-based access rules</li>
          <li>Usage generated by any third party accessing your publicly shared projects</li>
        </ul>
        <p>
          By making a project public or sharing it with others, you accept full liability for all usage incurred by any user who accesses that project. We strongly recommend configuring appropriate rate limits before sharing projects.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>7.5 Rate Limits</h3>
        <p>
          You may configure rate limits on your projects to control usage, including:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>Maximum queries per user per hour</li>
          <li>Maximum query depth per knowledge base</li>
        </ul>
        <p>
          It is your responsibility to configure appropriate rate limits to manage your usage and costs. We are not liable for any charges incurred due to unconfigured or misconfigured rate limits.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>7.6 Free Trials</h3>
        <p>
          We may, at our sole discretion, offer free trial usage to allow you to evaluate our services. Trial usage is subject to the following terms:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>Trial usage is provided for evaluation purposes only and may be limited in quantity or duration.</li>
          <li>Unused trial usage is automatically forfeited when you subscribe to a paid plan. Any remaining trial allowance at the point of conversion will not carry over.</li>
          <li>Trial usage that is not consumed before the end of the trial period will expire and be removed from your account.</li>
          <li>We reserve the right to modify or discontinue trial offers at any time without prior notice.</li>
        </ul>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>8. Payment Terms</h2>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>8.1 Subscription Billing</h3>
        <p>
          Stark Crawl operates on a prepaid subscription model. Your monthly allowance of queries and page imports is issued at the start of each billing period when your subscription payment succeeds. You may change your Essentials usage level at any time; changes take effect immediately — you are charged the new level&apos;s full price and your billing cycle restarts from that date. Plan changes are not prorated, but any remaining usage from the previous period stays available until it expires.
        </p>
        <p>
          For Scale clients, we may offer invoice-based billing where usage is billed monthly in arrears. If you are on an invoice billing arrangement, your allowance is allocated at the start of each billing period based on your agreement, and your usage from the previous period is reconciled and invoiced accordingly. Invoiced amounts are subject to applicable taxes including VAT.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>8.2 Payment Methods</h3>
        <p>
          Subscription plan fees are charged automatically to your nominated payment card at the start of each billing period. You can manage your saved cards, and which card pays your plan, in your account settings.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>8.3 Payment Processing</h3>
        <p>
          Payments are processed securely through Stripe. By providing payment information, you authorise us to charge your chosen payment method for subscription fees. You agree to provide accurate and complete payment information.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>8.4 Pricing</h3>
        <p>
          Current subscription prices and included usage allocations are available on our{' '}
          <a href="/pricing">pricing page</a>.
          The token consumption of operations varies depending on the AI model used to process your queries and content. Different operations consume your allowance at different rates.
        </p>
        <p>
          All fees are quoted in the currency specified at the point of purchase and are exclusive of VAT unless otherwise stated. Pricing may also vary based on your billing arrangement and subscription plan.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>8.5 Exhausted Allowance</h3>
        <p>
          If your usage allowance is exhausted, access to the affected AI functionality on your projects will be suspended until your next subscription billing period begins or you move to a higher usage level. Your uploaded content will remain stored but queries will not be processed.
        </p>
        <p>
          Any usage that was processed before your allowance was depleted but not yet reconciled will be recorded as overage. Overage represents a debt that must be settled. When your next subscription period begins or you move to a higher usage level, the overage amount will be deducted from your new allowance before it becomes available for use. For invoice-billed clients, overage will be included in your next monthly invoice.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>8.6 Usage Expiry and Refund Policy</h3>
        <p>
          Subscription fees are non-refundable except where required by law. Usage allowances are subject to the following expiry terms:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>
            <strong>Subscription usage</strong> expires at the end of the applicable billing period (typically one month from the date of issuance). Unused subscription usage does not roll over to the next billing period.
          </li>
          <li>
            <strong>Top-up credits</strong> purchased before top-ups were withdrawn remain valid for 12 months from the date of purchase.
          </li>
          <li>
            <strong>Trial usage</strong> is forfeited immediately upon subscribing to a paid plan, or at the end of the trial period, whichever occurs first.
          </li>
        </ul>
        <p>
          Subscription fees are non-refundable for partial billing periods. If you cancel a subscription mid-period, you will retain access to your plan until the end of the current billing period, after which the subscription will not renew.
        </p>
        <p>
          Upon account termination, any remaining allowance is forfeited. Expired or forfeited usage cannot be reinstated or refunded.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>8.7 Price Changes</h3>
        <p>
          We may change our subscription prices, included usage allocations, query depth limits, and token consumption rates from time to time. We will provide you with reasonable notice of any price changes before they take effect. Price changes will apply from the next billing period and will not affect allowances already issued.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>8.8 Failed Payments</h3>
        <p>
          If a payment or subscription charge fails, we may retry the charge. If payment cannot be processed, your access to AI functionality may be suspended until payment is successfully completed. We reserve the right to terminate your account for persistent payment failures.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>9. Intellectual Property</h2>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>9.1 Our Intellectual Property</h3>
        <p>
          Stark Crawl, including its software, design, logos, and documentation, is owned by Aduro Creative Ltd and is protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works based on our intellectual property without our prior written consent.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>9.2 Feedback</h3>
        <p>
          If you provide us with feedback, suggestions, or ideas about our services, you grant us a perpetual, irrevocable, royalty-free licence to use, modify, and incorporate that feedback into our services without any obligation to you.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>10. AI-Generated Responses</h2>
        <p>
          Stark Crawl uses artificial intelligence to generate responses based on Your Content. You acknowledge and agree that:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>AI-generated responses may not always be accurate, complete, or appropriate</li>
          <li>You are responsible for reviewing and verifying any AI-generated output before relying on it</li>
          <li>AI-generated responses should not be treated as professional, legal, financial, or medical advice</li>
          <li>We do not guarantee the accuracy or reliability of AI-generated content</li>
        </ul>
        <p>
          To the maximum extent permitted by law, we bear no liability for any AI-generated responses produced through our services, or for any loss or damage arising from reliance on them. Responsibility for the accuracy, completeness, and appropriateness of AI-generated responses lies with you as the account owner. This includes responses provided to end users who access your projects, whether via email-based access rules or public links, and it is your responsibility to communicate to those users that responses are AI-generated and may be inaccurate.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>11. Privacy and Data Protection</h2>
        <p>
          Our collection and use of personal data is governed by our{' '}
          <a href="/privacy">Privacy Policy</a>, which forms part of these Terms. By using our services, you acknowledge that you have read and understood our Privacy Policy.
        </p>
        <p>
          If you upload content containing personal data of third parties, you are the data controller for that data. You are responsible for ensuring you have a lawful basis for processing that data and for complying with applicable data protection laws.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>12. Data Processing Agreement</h2>
        <p>
          Where we process personal data on your behalf as a data processor, the Data Processing Agreement available at{' '}
          <a href="/dpa">starkcrawl.com/dpa</a>{' '}
          applies and forms part of these Terms.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>13. Disclaimers</h2>
        <p>
          To the maximum extent permitted by law:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>
            Our services are provided &quot;as is&quot; and &quot;as available&quot; without warranties of any kind, whether express, implied, or statutory
          </li>
          <li>
            We disclaim all warranties, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement
          </li>
          <li>
            We do not warrant that our services will be uninterrupted, error-free, or secure
          </li>
          <li>
            We do not warrant the accuracy, completeness, or usefulness of any AI-generated content
          </li>
        </ul>
        <p>
          Nothing in these Terms excludes or limits our liability for death or personal injury caused by our negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded or limited by English law.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>14. Limitation of Liability</h2>
        <p>
          Subject to the disclaimers above, to the maximum extent permitted by law:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>
            We shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, business, or goodwill
          </li>
          <li>
            Our total liability to you for any claims arising from or related to these Terms or our services shall not exceed the greater of: (a) the amount you paid us in the 12 months preceding the claim, or (b) £100
          </li>
        </ul>
        <p>
          These limitations apply regardless of the theory of liability, whether based on contract, tort (including negligence), strict liability, or any other basis.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>15. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless Aduro Creative Ltd and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or related to:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>Your use of our services</li>
          <li>Your Content or your sharing of content</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any rights of a third party</li>
        </ul>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>16. Suspension and Termination</h2>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>16.1 Termination by You</h3>
        <p>
          You may terminate your account at any time by contacting us at{' '}
          <a href="mailto:privacy@starkcrawl.com">privacy@starkcrawl.com</a>{' '}
          or through your account settings.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>16.2 Termination by Us</h3>
        <p>
          We may suspend or terminate your account at any time if:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>You breach these Terms</li>
          <li>You fail to pay applicable fees</li>
          <li>We are required to do so by law</li>
          <li>We decide to discontinue our services (with reasonable notice)</li>
        </ul>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>16.3 Effect of Termination</h3>
        <p>
          Upon termination, your right to use our services will cease immediately. We will delete Your Content in accordance with our data retention policy (within 90 days), unless we are required to retain it for legal purposes. Provisions that by their nature should survive termination will remain in effect.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>17. Changes to These Terms</h2>
        <p>
          We may modify these Terms from time to time. We will notify you of material changes by posting the updated Terms on our website and updating the &quot;Last updated&quot; date. For significant changes, we may also notify you by email.
        </p>
        <p>
          Your continued use of our services after the effective date of any changes constitutes your acceptance of the modified Terms. If you do not agree to the modified Terms, you must stop using our services and close your account.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>18. General Provisions</h2>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>18.1 Governing Law</h3>
        <p>
          These Terms are governed by and construed in accordance with the laws of England and Wales. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>18.2 Entire Agreement</h3>
        <p>
          These Terms, together with our Privacy Policy and any applicable Data Processing Agreement, constitute the entire agreement between you and us regarding our services and supersede any prior agreements.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>18.3 Severability</h3>
        <p>
          If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>18.4 Waiver</h3>
        <p>
          Our failure to enforce any right or provision of these Terms shall not constitute a waiver of that right or provision.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>18.5 Assignment</h3>
        <p>
          You may not assign or transfer your rights under these Terms without our prior written consent. We may assign our rights and obligations under these Terms without restriction.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>18.6 Force Majeure</h3>
        <p>
          We shall not be liable for any failure or delay in performing our obligations where such failure or delay results from circumstances beyond our reasonable control, including but not limited to acts of God, natural disasters, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, accidents, strikes, or shortages of transportation, facilities, fuel, energy, labour, or materials.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>19. Contact Us</h2>
        <p>
          If you have any questions about these Terms, please contact us:
        </p>
        <p>
          <strong>Email:</strong>{' '}
          <a href="mailto:privacy@starkcrawl.com">privacy@starkcrawl.com</a>
        </p>
        <p>
          <strong>Company:</strong> Aduro Creative Ltd (trading as Stark Crawl)
          <br />
          Company Number: 11200639
        </p>
        <p>
          <strong>Address:</strong> Aduro Creative Ltd (trading as Stark Crawl)
          <br />
          27 Old Gloucester Street, London, WC1N 3AX
        </p>
      </article>
    </section>
  );
}